import React, { useMemo, useState } from 'react';
import {
  curBookingsBookingNoSelector,
  curBookingsSelector,
} from 'store/selectors/cur-bookings.selector';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { curBookingsActions } from 'store/actions/cur-bookings.actions';
import TextField from '@material-ui/core/TextField';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import styled from 'styled-components/macro';
import {
  Button,
  IconButton,
  Paper,
  Popover,
} from '@material-ui/core';
import { AddCircleRounded } from '@material-ui/icons';

interface EditableBookingNoMenuProps {}

const optionsSelector = createSelector(
  curBookingsBookingNoSelector,
  (curBookings): OptionType[] =>
    curBookings.map((b, i) => ({ value: i, label: b })),
);

interface OptionType {
  inputValue?: string;
  value?: number;
  label: string;
}

const filter = createFilterOptions<OptionType>();

const EditableBookingNoMenu: React.FC<EditableBookingNoMenuProps> = () => {
  const options: OptionType[] = useSelector(optionsSelector);
  const { viewingIndex: curValue } = useSelector(curBookingsSelector);
  const dispatch = useDispatch();

  const valueOptionMap = useMemo(
    () =>
      options.reduce(
        (acc, option) => ({
          ...acc,
          [option.value as number]: option,
        }),
        {},
      ),
    [options],
  );

  const curValueAc: OptionType | null = useMemo(
    () =>
      (valueOptionMap &&
        curValue !== undefined &&
        curValue !== null &&
        valueOptionMap[curValue]) ||
      null,
    [curValue, valueOptionMap],
  );

  const handleChange = (event, newValue) => {
    console.debug(`newValue: `, newValue);
    if (typeof newValue === 'string') {
      newValue = newValue.trim();
      // come from hit enter not select
      const existed = Object.values<{
        label: string;
        value: string;
      }>(valueOptionMap).find((item) => item.label === newValue);
      if (existed) {
        // select the old one if existed
        dispatch(
          curBookingsActions.setViewingBookingIndex(existed.value),
        );
      } else {
        handleAddNewBookingNo(newValue);
      }
    } else if (newValue && newValue.inputValue) {
      // Create a new value from the user input
      handleAddNewBookingNo(newValue.inputValue);
    } else if (newValue !== null) {
      // when delete value newValue will be null
      dispatch(
        curBookingsActions.setViewingBookingIndex(newValue.value),
      );
    }
  };

  const [newBookingInput, setNewBookingInput] = useState('');

  const handleAddNewBookingNo = (
    value: string,
    closeNewBookingPanelAfter = false,
  ) => {
    const succeed = dispatch(
      curBookingsActions.addNewBookingNo(value, true),
    );
    console.debug(`succeed: `, succeed);
    if (succeed && closeNewBookingPanelAfter) {
      handleCloseNewBookingNoPanel();
    }
  };

  const handleChangeNewBookingInput = (e) => {
    setNewBookingInput(e.target.value);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  );

  const handleClickShowNewBookingButton = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    console.debug(`event.currentTarget: `, event.currentTarget);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseNewBookingNoPanel = () => {
    setAnchorEl(null);
  };

  return (
    <SEditableBookingNoMenu>
      <Autocomplete
        value={curValueAc}
        onChange={handleChange}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          // Suggest the creation of a new value
          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              label: `Add "${params.inputValue}"`,
            });
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        //   id="free-solo-with-text-demo" // need this?
        options={options}
        getOptionLabel={(option) => {
          // Value selected with enter, right from the input
          if (typeof option === 'string') {
            return option;
          }
          // Add "xxx" option created dynamically
          if (option.inputValue) {
            return option.inputValue;
          }
          // Regular option
          return option.label;
        }}
        renderOption={(option) => option.label}
        style={{ width: 300 }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select or Add New Booking No."
            variant="outlined"
          />
        )}
      />
      {/* add new booking button */}
      <SNewBookingWrapper>
        <IconButton
          onClick={handleClickShowNewBookingButton}
          aria-describedby="new-bookin-no-modal"
          color="primary"
          size="small"
        >
          <AddCircleRounded />
        </IconButton>
        <Popover
          id="new-bookin-no-modal"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseNewBookingNoPanel}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <SEnterNewBookingPaper>
            <TextField
              fullWidth
              label="Enter a new booking no."
              variant="outlined"
              autoFocus
              InputLabelProps={{
                shrink: true,
              }}
              onChange={handleChangeNewBookingInput}
              onKeyUp={(e: any) => {
                if (e.key === 'Enter') {
                  handleAddNewBookingNo(e.target.value, true);
                }
              }}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                handleAddNewBookingNo(newBookingInput, true);
              }}
            >
              Create
            </Button>
          </SEnterNewBookingPaper>
        </Popover>
      </SNewBookingWrapper>
    </SEditableBookingNoMenu>
  );
};

export default EditableBookingNoMenu;
const SEditableBookingNoMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;
const SNewBookingWrapper = styled.div`
  position: relative;
`;
const SEnterNewBookingPaper = styled(Paper)`
  padding: 0.5rem;
  width: 300px;
  display: grid;
  grid-template-columns: 1fr auto;
`;
